export type EclatCentre = {
  slug: string;
  label: string;
  addressLines: readonly string[];
  usageLine?: string;
  gbpShareUrl?: string;
  gbpReviewUrl?: string;
};

export type EclatLabLocation = {
  label: string;
  addressLines: readonly string[];
  usageLine?: string;
  gbpShareUrl?: string;
  gbpReviewUrl?: string;
  /** Slug for an internal location page (e.g. "henderson"). Omit for map-only venues. */
  slug?: "henderson" | "orchard";
  /** Human-readable opening timeframe for venues that are not open yet. */
  opening?: string;
  /** Practical subjects the venue will run. */
  subjects?: readonly string[];
};

export const MAIN_CENTRE = {
  slug: "jurong-east",
  label: "Jurong East Centre (Vision Exchange)",
  addressLines: ["2 Venture Dr, #16-07 Vision Exchange", "Singapore 608526"],
  gbpShareUrl: "https://maps.app.goo.gl/V38iGy8qPFnj9Z5G8",
  gbpReviewUrl: "https://g.page/r/CZ0HqPJ05sZOEBM/review",
} as const satisfies EclatCentre;

export const ONE_NORTH_EVENTS_OFFICE = {
  slug: "one-north",
  label: "one-north Events Office",
  addressLines: ["67 Ayer Rajah Crescent, #02-14", "Singapore 139950"],
  usageLine: "Talks and presentations only. No regular lessons.",
  gbpShareUrl: "https://maps.app.goo.gl/MpcUENgiLfeTQxxY7",
  gbpReviewUrl: "https://g.page/r/CQFFJTRcu0FYEBM/review",
} as const satisfies EclatCentre;

export const ECLAT_CENTRES: readonly EclatCentre[] = [MAIN_CENTRE] as const;

export const ECLAT_LOCATIONS: readonly EclatCentre[] = [
  MAIN_CENTRE,
  ONE_NORTH_EVENTS_OFFICE,
] as const;

const VISION_EXCHANGE_LAB: EclatLabLocation = {
  label: MAIN_CENTRE.label,
  addressLines: MAIN_CENTRE.addressLines,
  usageLine:
    "All practical subjects, on weekdays only. Convenient for the west.",
  gbpShareUrl: MAIN_CENTRE.gbpShareUrl,
  gbpReviewUrl: MAIN_CENTRE.gbpReviewUrl,
} as const;

export const ORCHARD_PHYSICS_LAB: EclatLabLocation = {
  slug: "orchard",
  label: "Orchard Physics Venue",
  addressLines: ["150 Orchard Rd", "Singapore 238841"],
  usageLine: "Physics practicals only.",
  gbpShareUrl: "https://share.google/1frrKemNw2ClF4lgs",
  gbpReviewUrl: "https://g.page/r/CY-uZ8WMSCG8EBM/review",
} as const;

export const HENDERSON_LAB: EclatLabLocation = {
  slug: "henderson",
  label: "Henderson Practical Lab",
  addressLines: ["221 Henderson Road #05-09", "Singapore 159557"],
  usageLine:
    "Our dedicated lab centre for Chemistry, Physics and Biology practicals, open throughout the week.",
  subjects: ["Chemistry", "Physics", "Biology"],
  // Read from the 221 Henderson Road Business Profile on 29 October 2026.
  gbpReviewUrl: "https://g.page/r/CbRnEUvY46WkEAI/review",
} as const;

// Henderson leads: it is the dedicated lab centre (client direction, 27 October 2026).
export const LAB_LOCATIONS: readonly EclatLabLocation[] = [
  HENDERSON_LAB,
  VISION_EXCHANGE_LAB,
  ORCHARD_PHYSICS_LAB,
] as const;

export const UPCOMING_LAB_LOCATIONS: readonly EclatLabLocation[] = [] as const;

export type LabSubject = "chemistry" | "physics" | "biology";

export type LabTimingSlot = {
  label: string;
  startTime: string;
  endTime: string;
};

export type LabTimingRow = {
  day: "Weekdays" | "Weekends";
  hours: string;
  schemaDays: readonly string[];
  slots: readonly LabTimingSlot[];
};

export type LabTimingGroup = {
  key: "henderson" | "vision-exchange";
  venue: string;
  schedule: readonly LabTimingRow[];
};

export const LAB_VENUES_BY_SUBJECT: Record<
  LabSubject,
  readonly EclatLabLocation[]
> = {
  chemistry: [HENDERSON_LAB, VISION_EXCHANGE_LAB],
  physics: [HENDERSON_LAB, VISION_EXCHANGE_LAB, ORCHARD_PHYSICS_LAB],
  biology: [HENDERSON_LAB, VISION_EXCHANGE_LAB],
} as const;

const WEEKDAYS = [
  "https://schema.org/Monday",
  "https://schema.org/Tuesday",
  "https://schema.org/Wednesday",
  "https://schema.org/Thursday",
  "https://schema.org/Friday",
] as const;

const WEEKENDS = [
  "https://schema.org/Saturday",
  "https://schema.org/Sunday",
] as const;

export const LAB_TIMINGS_LAST_UPDATED = {
  iso: "2026-10-27",
  label: "27 October 2026",
} as const;

export const HENDERSON_LAB_TIMINGS = {
  key: "henderson",
  venue: HENDERSON_LAB.label,
  schedule: [
    {
      day: "Weekdays",
      hours: "12 noon to 2pm, 2pm to 4pm, or 4pm to 6pm",
      schemaDays: WEEKDAYS,
      slots: [
        { label: "12 noon to 2pm", startTime: "12:00", endTime: "14:00" },
        { label: "2pm to 4pm", startTime: "14:00", endTime: "16:00" },
        { label: "4pm to 6pm", startTime: "16:00", endTime: "18:00" },
      ],
    },
    {
      day: "Weekends",
      hours: "12 noon to 2pm, 2pm to 4pm, 4pm to 6pm, or 6pm to 8pm",
      schemaDays: WEEKENDS,
      slots: [
        { label: "12 noon to 2pm", startTime: "12:00", endTime: "14:00" },
        { label: "2pm to 4pm", startTime: "14:00", endTime: "16:00" },
        { label: "4pm to 6pm", startTime: "16:00", endTime: "18:00" },
        { label: "6pm to 8pm", startTime: "18:00", endTime: "20:00" },
      ],
    },
  ],
} as const satisfies LabTimingGroup;

export const VISION_EXCHANGE_LAB_TIMINGS = {
  key: "vision-exchange",
  venue: MAIN_CENTRE.label,
  schedule: [
    {
      // Practical sessions only; weekly theory classes keep THEORY_TIMINGS.
      day: "Weekdays",
      hours: "11am to 1pm or 2pm to 4pm",
      schemaDays: WEEKDAYS,
      slots: [
        { label: "11am to 1pm", startTime: "11:00", endTime: "13:00" },
        { label: "2pm to 4pm", startTime: "14:00", endTime: "16:00" },
      ],
    },
  ],
} as const satisfies LabTimingGroup;

export const LAB_TIMING_GROUPS = [
  HENDERSON_LAB_TIMINGS,
  VISION_EXCHANGE_LAB_TIMINGS,
] as const satisfies readonly LabTimingGroup[];

export const LAB_TIMINGS_LABEL = LAB_TIMING_GROUPS.map(
  (group) =>
    `${group.venue}: ${group.schedule
      .map((row) => `${row.day} ${row.hours}`)
      .join("; ")}`
).join(". ");

export const THEORY_TIMINGS = [
  { day: "Monday", hours: "4-9pm" },
  { day: "Tuesday", hours: "4-9pm" },
  { day: "Wednesday", hours: "4-9pm" },
  { day: "Thursday", hours: "4-9pm" },
  { day: "Friday", hours: "Closed" },
  { day: "Saturday", hours: "9am-6pm" },
  { day: "Sunday", hours: "9am-6pm" },
] as const;

const DAY_ABBREVIATIONS: Record<
  (typeof THEORY_TIMINGS)[number]["day"],
  string
> = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
} as const;

function toDayRangeLabel(
  first: (typeof THEORY_TIMINGS)[number]["day"],
  last: (typeof THEORY_TIMINGS)[number]["day"]
) {
  if (first === last) return DAY_ABBREVIATIONS[first];
  return `${DAY_ABBREVIATIONS[first]}-${DAY_ABBREVIATIONS[last]}`;
}

export const THEORY_TIMINGS_GROUPED = (() => {
  const groups: Array<{
    start: (typeof THEORY_TIMINGS)[number]["day"];
    end: (typeof THEORY_TIMINGS)[number]["day"];
    hours: string;
  }> = [];

  for (const entry of THEORY_TIMINGS) {
    const last = groups.at(-1);
    if (!last || last.hours !== entry.hours) {
      groups.push({ start: entry.day, end: entry.day, hours: entry.hours });
      continue;
    }
    last.end = entry.day;
  }

  return groups.map((group) => ({
    days: toDayRangeLabel(group.start, group.end),
    hours: group.hours,
  }));
})();

export const LAB_PRICING = {
  currency: "SGD",
  aLevelPerLesson: { min: 210, max: 290 },
  oLevelPerLesson: { min: 120, max: 200 },
} as const;

export function toGoogleMapsSearchUrl(
  queryOrLines: string | readonly string[]
) {
  const query: string =
    typeof queryOrLines === "string" ? queryOrLines : queryOrLines.join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query
  )}`;
}
